import { MapPin, Navigation, Car } from "lucide-react";

// Map background images from design guidelines
const MAP_IMAGES = [
  "https://images.unsplash.com/photo-1747566068635-9937c15f4f03?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjaXR5JTIwbWFwJTIwdmlldyUyMHRvcCUyMGRvd258ZW58MHx8fHwxNzY4NzU3NDI4fDA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1698948233640-fe50ca4ce8c7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBjaXR5JTIwbWFwJTIwdmlldyUyMHRvcCUyMGRvd258ZW58MHx8fHwxNzY4NzU3NDI4fDA&ixlib=rb-4.1.0&q=85"
];

export default function MockMap({ ride, rides, showDriver, showMultiple, variant = "light" }) {
  const isDark = variant === "dark";
  const mapImage = MAP_IMAGES[0];

  // Generate random positions for markers
  const getRandomPosition = (seed = 0) => ({
    top: `${20 + (seed * 17) % 60}%`,
    left: `${15 + (seed * 23) % 70}%`
  });

  return (
    <div 
      className="map-container w-full h-full relative"
      style={{ 
        backgroundImage: `url(${mapImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
      data-testid="mock-map"
    >
      {/* Overlay */}
      <div className={`absolute inset-0 ${
        isDark 
          ? "bg-gradient-to-b from-slate-950/40 to-slate-950/70" 
          : "bg-gradient-to-b from-white/10 to-white/30"
      }`} />

      {/* Mock Map Badge */}
      <div className={`absolute top-3 left-3 px-2 py-1 rounded text-xs font-medium ${
        isDark ? "bg-slate-900/80 text-slate-400" : "bg-white/80 text-slate-600"
      }`}>
        Mock Map Mode
      </div>

      {/* Single Ride View */}
      {ride && !showMultiple && (
        <>
          {/* Pickup Markers */}
          {ride.pickup_locations?.map((loc, index) => (
            <div
              key={index}
              className="absolute transform -translate-x-1/2 -translate-y-1/2 z-10"
              style={getRandomPosition(index + 1)}
            >
              <div className="pickup-marker">
                <span className="text-xs font-bold text-white">{index + 1}</span>
              </div>
              <div className={`mt-1 px-2 py-0.5 rounded text-xs whitespace-nowrap ${
                isDark ? "bg-slate-900/90 text-slate-300" : "bg-white/90 text-slate-700"
              }`}>
                Pickup {index + 1}
              </div>
            </div>
          ))}

          {/* Dropoff Marker */}
          <div
            className="absolute transform -translate-x-1/2 -translate-y-1/2 z-10"
            style={getRandomPosition(99)}
          >
            <div className="dropoff-marker">
              <MapPin className="w-4 h-4 text-white" />
            </div>
            <div className={`mt-1 px-2 py-0.5 rounded text-xs whitespace-nowrap ${
              isDark ? "bg-slate-900/90 text-slate-300" : "bg-white/90 text-slate-700"
            }`}>
              Dropoff
            </div>
          </div>

          {/* Driver Marker */}
          {showDriver && ride.current_latitude && (
            <div
              className="absolute transform -translate-x-1/2 -translate-y-1/2 z-20"
              style={{ top: "50%", left: "45%" }}
            >
              <div className="driver-marker">
                <Car className="w-5 h-5 text-slate-900" />
              </div>
              <div className={`mt-1 px-2 py-0.5 rounded text-xs whitespace-nowrap ${
                isDark ? "bg-lime-400 text-slate-900" : "bg-indigo-900 text-white"
              }`}>
                Driver
              </div>
            </div>
          )}

          {/* Route Line (simplified) */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none z-5">
            <defs>
              <linearGradient id="routeGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#3b82f6" />
                <stop offset="100%" stopColor="#ef4444" />
              </linearGradient>
            </defs>
            <path
              d="M 100 120 Q 200 80 250 150 T 350 200"
              fill="none"
              stroke="url(#routeGradient)"
              strokeWidth="3"
              strokeDasharray="8 4"
              opacity="0.7"
            />
          </svg>
        </>
      )}

      {/* Multiple Rides View (Employer) */}
      {showMultiple && rides && (
        <>
          {rides.map((r, index) => (
            <div
              key={r.id}
              className="absolute transform -translate-x-1/2 -translate-y-1/2 z-10"
              style={getRandomPosition(index * 7)}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                r.status === "in_progress" 
                  ? "bg-purple-500 animate-pulse" 
                  : r.status === "arrived"
                  ? "bg-lime-500"
                  : "bg-blue-500"
              }`}>
                <Car className="w-4 h-4 text-white" />
              </div>
            </div>
          ))}
        </>
      )}

      {/* Empty State */}
      {!ride && !rides?.length && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className={`text-center ${isDark ? "text-slate-500" : "text-slate-600"}`}>
            <Navigation className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No active rides to display</p>
          </div>
        </div>
      )}
    </div>
  );
}
